import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useAuth } from "../../contexts/auth.context";
import { recordFirstTouch } from "../../lib/analytics/first-touch.client";
import {
  resetLearningIdentity,
  syncLearningIdentity,
} from "../../lib/analytics/identity.client";

/**
 * 「本人である」ことが確かめられたあらゆる場面 (ログイン後の読み込み、
 * メール確認後の refreshAuth、OAuth 復帰、別タブでログインして戻った
 * focus) で一度だけ、PostHog の identify と learning_events の claim を
 * 呼ぶ。認証状態の唯一の写しである useAuth にだけ乗るので、個別の
 * 画面ごとに配線を足さなくて済む。
 *
 * 画面の描画・遷移は一切待たせない。裏側は全部 fire-and-forget。
 */
/**
 * 管理画面 (/admin 以下) では名乗らない。名乗りは「匿名で来た人が登録・ログインした
 * 瞬間に、その前の閲覧を本人に結ぶ」ための獲得の計測で、運営が受講生の一覧を
 * 開いただけで /api/learning-events/claim を打つ理由が無い。管理画面から出たとき
 * (pathname の変化) に、まだなら名乗る
 */
function isAdminPath(pathname: string): boolean {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

export function AnalyticsIdentitySync() {
  const { user, isLoading } = useAuth();
  const onAdmin = isAdminPath(useLocation().pathname);
  const userId = user?.id ?? null;
  // user オブジェクトは setUser ごとに作り直される。同じ人での再実行を
  // 止めるのは id のほう
  const syncedUserIdRef = useRef<string | null>(null);
  const hadUserRef = useRef(false);

  // 獲得経路は「最初に見せたページ」で控える。登録のあとで URL を
  // 見ても手遅れなので、認証の解決を待たずに mount で 1 度だけ
  useEffect(() => {
    recordFirstTouch();
  }, []);

  useEffect(() => {
    if (userId) {
      hadUserRef.current = true;
      if (onAdmin) return;
      if (syncedUserIdRef.current === userId) return;
      syncedUserIdRef.current = userId;
      void syncLearningIdentity(userId);
      return;
    }
    // 解決済みで user が null = ログアウトが確かめられた瞬間。
    // まだ誰も確かめられていない初回の null (anonymous) では触らない。
    // そこで reset すると、匿名のまま戻ってきた人の継続した distinct_id を
    // 捨ててしまい、匿名期の出来事を名乗る鍵を失う
    if (!isLoading && hadUserRef.current) {
      hadUserRef.current = false;
      syncedUserIdRef.current = null;
      resetLearningIdentity();
    }
  }, [userId, isLoading, onAdmin]);

  return null;
}
